"use client";

/* «Atlante · <Paese>.»: la carta schematica e l'elenco dei nodi, rifatti il
   25/10/2026 su uploads/atlante-turchia-riferimento.html con le istruzioni
   uploads/ISTRUZIONI-AGENTE-atlante-paese.md.

   Prima la carta era 460 × 270 e l'elenco a destra molto più alto: sopra e
   sotto restavano due fasce vuote. Adesso la colonna sinistra è una colonna
   flex alta quanto la destra (didascalia, carta, legenda), con il fondo
   millimetrato su tutta l'altezza; la carta della Turchia è 480 × 520, quasi
   quadrata come il riquadro, e sta in `absolute inset-0` dentro una scatola
   `flex-1`: un `<svg>` con viewBox in una colonna flex si porterebbe dietro
   la sua altezza propria (838 pixel a 1440) e allungherebbe la riga.

   Client per un motivo solo: la voce accesa (`attivo`, il numero della voce
   o `null`), condivisa da elenco e carta. Passando su una voce o su un
   tracciato, o col fuoco, il contenitore prende `data-attivo` e le cose
   accese `data-acceso`: il resto (tracciato rame, gli altri al 18%, i
   punti spenti al 35%) lo fa il CSS `.oi-pa-at*` di globals.css, quello del
   modello. Il modello scriveva nove selettori per numero; qui il confronto
   lo fa React, così vale per qualunque numero di voci. Lo stato iniziale è
   `null`, quindi server e idratazione coincidono.

   Senza carta (Paesi senza la voce della redazione, ma con nodi) la stessa
   impaginazione col millimetrato e il nome del Paese grande in rame.

   Dal 26/10/2026 lo usa anche «Atlante di stretti e corridoi» della prima
   pagina (components/home/Atlante.tsx, richiesta del committente: la
   scheda della Turchia anche lì), con una carta senza Paese, le `rotte`
   che non sono di nessun nodo e `totale` = i nodi di tutto l'atlante. */

import Link from "next/link";
import { useState, type CSSProperties } from "react";

import { RIMANDO } from "@/components/home/stile";
import type { NodoAtlantePaese } from "@/lib/paese-dati";
import { LEGENDA_LINEE, type CartaPaese as Carta, type StileLinea } from "@/lib/paesi-pagina";

const ORDINE_STILI = Object.keys(LEGENDA_LINEE) as StileLinea[];

export function CartaPaese({
  nodi,
  carta,
  frase,
  nome,
  totale,
}: {
  nodi: NodoAtlantePaese[];
  carta: Carta | null;
  /** «nodi dell'atlante che passano dalla Turchia: stretti, corridoi…». */
  frase: string;
  /** Il nome del Paese, grande in rame quando non c'è la carta. */
  nome: string;
  /** Il numero grande accanto alla frase, quando non sono le voci
   *  dell'elenco (la prima pagina: i 65 nodi dell'atlante, sei in elenco). */
  totale?: number;
}) {
  const [attivo, setAttivo] = useState<number | null>(null);
  /* Il riquadro sulla carta sfuma quando si esce: tiene il testo dell'ultima
     voce, altrimenti si svuoterebbe prima di sparire. */
  const [ultimo, setUltimo] = useState<number | null>(null);
  const accendi = (n: number | null) => {
    setAttivo(n);
    if (n !== null) setUltimo(n);
  };
  const eventi = (n: number) => ({
    onMouseEnter: () => accendi(n),
    onFocus: () => accendi(n),
    onMouseLeave: () => accendi(null),
    onBlur: () => accendi(null),
  });

  const numero = new Map(nodi.map((x, i) => [x.slug, i + 1]));
  const slugAttivo = attivo !== null ? nodi[attivo - 1]?.slug : undefined;
  const tracciati = carta
    ? carta.nodi.flatMap((c) => {
        const n = numero.get(c.slug);
        return n && c.linea ? [{ n, slug: c.slug, linea: c.linea, nome: nodi[n - 1]!.nome }] : [];
      })
    : [];
  const rotte = carta?.rotte ?? [];
  const stili = ORDINE_STILI.filter(
    (s) => tracciati.some((t) => t.linea.stile === s) || rotte.some((r) => r.stile === s),
  );
  const vb = carta ? `${carta.viewBox.x ?? 0} ${carta.viewBox.y ?? 0} ${carta.viewBox.w} ${carta.viewBox.h}` : "";
  const info = ultimo !== null ? nodi[ultimo - 1] : undefined;

  return (
    <div
      className="oi-pa-at grid grid-cols-1 border border-t-4 border-rule border-t-segnale bg-surface min-[901px]:grid-cols-[minmax(0,7fr)_minmax(320px,5fr)]"
      data-attivo={attivo ?? undefined}
    >
      <div className="oi-pa-at-mappa relative flex flex-col border-b border-rule min-[901px]:min-h-[560px] min-[901px]:border-r min-[901px]:border-b-0">
        <div className="flex flex-wrap justify-between gap-x-3 gap-y-1 px-4 pt-4 font-mono [&>span]:whitespace-nowrap text-[10.5px] font-medium tracking-[0.1em] text-sommesso uppercase min-[601px]:px-[22px]">
          {carta ? (
            <>
              <span>
                <b className="font-semibold text-segnale" aria-hidden>
                  ●
                </b>{" "}
                {carta.principale} · nodo principale
              </span>
              <span>Carta schematica, non in scala</span>
            </>
          ) : (
            <span>Carta in preparazione</span>
          )}
        </div>
        <div
          className={`relative min-[901px]:min-h-0 min-[901px]:flex-1 ${
            carta ? "aspect-[var(--pa-rapporto)] min-[901px]:aspect-auto" : "min-h-[220px]"
          }`}
          style={carta ? ({ "--pa-rapporto": `${carta.viewBox.w} / ${carta.viewBox.h}` } as CSSProperties) : undefined}
        >
          {carta ? (
            <>
              <svg
                className="oi-pa-at-carta absolute inset-0 block h-full w-full"
                viewBox={vb}
                preserveAspectRatio="xMidYMid meet"
                role="img"
                aria-labelledby="pa-atlante-carta"
              >
                <title id="pa-atlante-carta">{carta.descrizione}</title>
                {carta.paese?.sagoma ? <path className="oi-pa-at-terra" d={carta.paese.sagoma} /> : null}
                {carta.paese?.riquadro ? (
                  <rect
                    className="oi-pa-at-terra"
                    x={carta.paese.riquadro.x}
                    y={carta.paese.riquadro.y}
                    width={carta.paese.riquadro.w}
                    height={carta.paese.riquadro.h}
                    rx="14"
                  />
                ) : null}
                {carta.paese ? (
                  <text className="oi-pa-at-paese" x={carta.paese.tx} y={carta.paese.ty} textAnchor="middle">
                    {carta.paese.testo}
                  </text>
                ) : null}
                {carta.mari.map((m) => (
                  <text key={m.testo} className="oi-pa-at-mare" x={m.x} y={m.y} textAnchor={m.ancora ?? "start"}>
                    {m.testo}
                  </text>
                ))}
                {rotte.map((r) => (
                  <g key={r.d} className={`oi-pa-at-r oi-pa-at-${r.stile}`} aria-hidden="true">
                    <path className="oi-pa-at-tr" d={r.d} />
                  </g>
                ))}
                {carta.bordi.map((b) => (
                  <text key={b.testo} className="oi-pa-at-dir" x={b.x} y={b.y} textAnchor={b.ancora ?? "start"}>
                    {b.testo}
                  </text>
                ))}
                {tracciati.map((t) => (
                  <Link
                    key={t.slug}
                    href={`/corridoi/${t.slug}`}
                    className={`oi-pa-at-r oi-pa-at-${t.linea.stile}`}
                    aria-label={t.nome}
                    data-n={t.n}
                    data-acceso={attivo === t.n ? "" : undefined}
                    {...eventi(t.n)}
                  >
                    <path className="oi-pa-at-hit" d={t.linea.d} />
                    <path className="oi-pa-at-tr" d={t.linea.d} />
                  </Link>
                ))}
                {carta.punti.map((p) => (
                  <g
                    key={`${p.x} ${p.y}`}
                    className={`oi-pa-at-nodo${p.principale ? " oi-pa-at-princ" : ""}`}
                    data-acceso={slugAttivo && p.nodi.includes(slugAttivo) ? "" : undefined}
                  >
                    {p.principale ? <circle className="oi-pa-at-pulsa" cx={p.x} cy={p.y} r="6" /> : null}
                    <circle className="oi-pa-at-pt" cx={p.x} cy={p.y} r="4.2" />
                    <text x={p.tx} y={p.ty} textAnchor={p.ancora}>
                      {p.testo}
                    </text>
                  </g>
                ))}
              </svg>
              <div className="oi-pa-at-info hidden min-[901px]:block" aria-hidden="true">
                {info ? (
                  <>
                    <b className="block font-serif text-[18px] leading-[1.25] font-normal text-ink">
                      <i className="mr-2 font-mono text-[10.5px] font-semibold tracking-[0.1em] text-segnale not-italic uppercase">
                        {String(ultimo).padStart(2, "0")} · {info.tipo}
                      </i>
                      {info.nome}
                    </b>
                    <span className="font-sans text-[14px] text-sommesso">{info.frase}</span>
                  </>
                ) : null}
              </div>
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center px-6">
              <span className="text-center font-serif text-[clamp(44px,6vw,88px)] leading-none text-segnale">{nome}</span>
            </div>
          )}
        </div>
        {stili.length ? (
          <p className="oi-pa-at-leg m-0 flex flex-wrap gap-x-[18px] gap-y-1.5 px-4 pt-3 pb-[18px] font-sans text-[13px] text-mare min-[601px]:px-[22px]">
            {stili.map((s) => (
              <span key={s} className="inline-flex items-center gap-[7px]">
                <svg viewBox="0 0 26 6" aria-hidden="true" className={`oi-pa-at-campione oi-pa-at-${s} h-[6px] w-[26px]`}>
                  <line x1="1" y1="3" x2="25" y2="3" />
                </svg>
                {carta?.legenda?.[s] ?? LEGENDA_LINEE[s]}
              </span>
            ))}
          </p>
        ) : (
          <div className="pb-[18px]" />
        )}
      </div>
      <div className="flex flex-col px-4 py-5 min-[601px]:px-7 min-[601px]:pt-[26px] min-[601px]:pb-[22px]">
        <div className="flex items-baseline gap-3.5 pb-4">
          <span className="font-serif text-[52px] leading-[0.9] text-mare">{totale ?? nodi.length}</span>
          <span className="font-sans text-[15px] leading-[1.5] text-sommesso">{frase}</span>
        </div>
        <ol className="m-0 flex flex-1 list-none flex-col p-0">
          {nodi.map((x, i) => {
            const n = i + 1;
            return (
              <li key={x.slug} className="flex flex-1 border-t border-rule last:border-b">
                <Link
                  href={x.href}
                  data-n={n}
                  data-acceso={attivo === n ? "" : undefined}
                  className={`oi-pa-at-v relative grid flex-1 grid-cols-[30px_minmax(0,1fr)_auto] content-center gap-x-3 gap-y-0.5 py-2.5 pr-1.5 text-ink no-underline${
                    x.tipo === "In trattativa" ? " oi-pa-at-v-tratt" : ""
                  }`}
                  {...eventi(n)}
                >
                  <span className="pt-[5px] font-mono text-[11px] font-medium text-sommesso">{String(n).padStart(2, "0")}</span>
                  <span className="font-serif text-[19px] leading-[1.25]">{x.nome}</span>
                  <span className="oi-pa-at-tipo pt-[5px] font-mono text-[10.5px] font-medium tracking-[0.08em] uppercase">
                    {x.tipo}
                  </span>
                  <span className="col-[2/-1] font-sans text-[14.5px] leading-[1.5] text-sommesso">{x.frase}</span>
                </Link>
              </li>
            );
          })}
        </ol>
        <p className="m-0 pt-4">
          <Link href="/corridoi" className={RIMANDO}>
            Esplora l’atlante <span aria-hidden>↗</span>
          </Link>
        </p>
      </div>
    </div>
  );
}
